import React, { useState, useEffect } from 'react';
import { FaTimes, FaProjectDiagram, FaSave, FaSpinner, FaInfoCircle, FaLock } from 'react-icons/fa';
import { useAuth } from '../../provider/auth.provider';

interface Project {
  id: number;
  project_name: string;
  status: string;
  created_at: string;
}

interface Activity {
  id: number;
  activity_name: string;
  activity_description: string;
  /** Days an SE gets to finish field work before the site blocks new
   *  assignments. null / 0 = no deadline for this activity. */
  site_completion_deadline_days?: number | null;
  status: 'ACTIVE' | 'SUSPENDED';
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by?: number;
  suspended_by?: number;
  suspended_at?: string;
  projects?: Project[];
  project_count?: number;
}

interface ActivityFormData {
  activity_name: string;
  activity_description: string;
  /** Kept as a string so the input can be cleared; '' means "no deadline". */
  site_completion_deadline_days: string;
  status?: 'ACTIVE' | 'SUSPENDED';
}

interface EditActivityFormData extends ActivityFormData {
  id: number;
}

// Format date
  export const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

interface EditActivityOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EditActivityFormData) => void;
  activity: Activity | null;
  loading?: boolean;
}

export const EditActivityOffcanvas: React.FC<EditActivityOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  activity,
  loading = false
}) => {
  const { hasPermission } = useAuth();
  // Only a role holding controls / stop_survey_assignment_to_se may change the
  // deadline — it decides whether an engineer can be handed new work at all.
  const canEditDeadline = hasPermission('controls', 'stop_survey_assignment_to_se');

  const [formData, setFormData] = useState<ActivityFormData>({
    activity_name: '',
    activity_description: '',
    site_completion_deadline_days: '',
    status: 'ACTIVE'
  });

  const [errors, setErrors] = useState<Partial<ActivityFormData>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof ActivityFormData, boolean>>>({});

  // Populate form when activity changes
  useEffect(() => {
    if (activity && isOpen) {
      setFormData({
        activity_name: activity.activity_name || '',
        activity_description: activity.activity_description || '',
        site_completion_deadline_days:
          activity.site_completion_deadline_days === null ||
          activity.site_completion_deadline_days === undefined
            ? ''
            : String(activity.site_completion_deadline_days),
        status: activity.status || 'ACTIVE'
      });
      setErrors({});
      setTouched({});
    }
  }, [activity, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setTouched({});
    }
  }, [isOpen]);

  // Validation
  const validateField = (name: keyof ActivityFormData, value: string) => {
    switch (name) {
      case 'activity_name':
        if (!value.trim()) {
          return 'Activity name is required';
        }
        if (value.trim().length < 3) {
          return 'Activity name must be at least 3 characters';
        }
        if (value.trim().length > 100) {
          return 'Activity name cannot exceed 100 characters';
        }
        return '';
      case 'activity_description':
        if (value.trim().length > 500) {
          return 'Description cannot exceed 500 characters';
        }
        return '';
      case 'site_completion_deadline_days': {
        if (!value.trim()) return '';
        if (!/^\d+$/.test(value.trim())) {
          return 'Enter a whole number of days, or leave blank for no deadline';
        }
        if (Number(value) > 365) {
          return 'Deadline cannot exceed 365 days';
        }
        return '';
      }
      default:
        return '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    let isValid = true;

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      const fieldKey = key as keyof ActivityFormData;
      const error = validateField(fieldKey, formData[fieldKey] as string);
      if (error) {
        newErrors[fieldKey] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof ActivityFormData;

    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // Handle blur events for validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof ActivityFormData;

    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));

    const error = validateField(fieldName, value);
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!activity) return;

    // Mark all fields as touched
    setTouched({
      activity_name: true,
      activity_description: true,
      site_completion_deadline_days: true,
      status: true
    });

    if (validateForm()) {
      onSubmit({
        ...formData,
        id: activity.id
      });
    }
  };

  // Check if form has changes
  const hasChanges = activity ? (
    formData.activity_name !== activity.activity_name ||
    formData.activity_description !== activity.activity_description ||
    formData.site_completion_deadline_days !==
      (activity.site_completion_deadline_days === null ||
      activity.site_completion_deadline_days === undefined
        ? ''
        : String(activity.site_completion_deadline_days)) ||
    formData.status !== activity.status
  ) : false;

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);


  if (!isOpen || !activity) return null;

  const activeProjects = activity.projects?.filter(p => p.status === 'ACTIVE').length || 0;
  const canSuspend = activeProjects === 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={!loading ? onClose : undefined}
      />

      {/* Offcanvas */}
      <div className="overflow-y-auto fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Edit Activity</h2>
            <p className="text-sm text-gray-600 mt-1">Last updated: {formatDate(activity.updated_at)}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-6">
            {/* Activity Info */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
              <h4 className="text-sm font-medium text-gray-800 mb-2">Activity Information</h4>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-gray-500">Created:</span>
                  <p className="font-medium">{formatDate(activity.created_at)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Last Updated:</span>
                  <p className="font-medium">{formatDate(activity.updated_at)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Projects:</span>
                  <p className="font-medium">{activity.project_count || 0}</p>
                </div>
                <div>
                  <span className="text-gray-500">Active Projects:</span>
                  <p className="font-medium">{activeProjects}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Activity Name */}
              <div>
                <label htmlFor="activity_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Activity Name *
                </label>
                <input
                  type="text"
                  id="activity_name"
                  name="activity_name"
                  value={formData.activity_name}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.activity_name && touched.activity_name
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                    } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  placeholder="Enter activity name"
                  maxLength={100}
                />
                {errors.activity_name && touched.activity_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.activity_name}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.activity_name.length}/100 characters
                </p>
              </div>

              {/* Activity Description */}
              <div>
                <label htmlFor="activity_description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="activity_description"
                  name="activity_description"
                  value={formData.activity_description}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${errors.activity_description && touched.activity_description
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                    } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  placeholder="Enter activity description (optional)"
                  maxLength={500}
                />
                {errors.activity_description && touched.activity_description && (
                  <p className="mt-1 text-sm text-red-600">{errors.activity_description}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.activity_description.length}/500 characters
                </p>
              </div>

              {/* Site completion deadline — the "Stop Survey Assignment to SE"
                  control. Read-only without the permission so everyone can
                  still see the rule the assignment screen enforces. */}
              <div>
                <label
                  htmlFor="site_completion_deadline_days"
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"
                >
                  Site Completion Deadline (Days)
                  {!canEditDeadline && <FaLock className="w-3 h-3 text-gray-400" />}
                </label>
                <input
                  type="number"
                  id="site_completion_deadline_days"
                  name="site_completion_deadline_days"
                  value={formData.site_completion_deadline_days}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={loading || !canEditDeadline}
                  min={0}
                  max={365}
                  step={1}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_completion_deadline_days && touched.site_completion_deadline_days
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                    } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  placeholder="e.g. 3 — blank for no deadline"
                />
                {errors.site_completion_deadline_days && touched.site_completion_deadline_days && (
                  <p className="mt-1 text-sm text-red-600">{errors.site_completion_deadline_days}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Once a site engineer has had a site of this activity open in the field for
                  longer than this, they cannot be assigned another site until they complete it.
                  Leave blank (or 0) to switch the rule off for this activity.
                </p>
                {!canEditDeadline && (
                  <p className="mt-1 text-xs text-gray-400">
                    Changing this needs the &ldquo;Stop Survey Assignment to SE&rdquo; permission.
                  </p>
                )}
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={loading || (!canSuspend && formData.status === 'ACTIVE')}
                  className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPENDED">Suspended</option>
                </select>

                {!canSuspend && formData.status === 'ACTIVE' && (
                  <div className="mt-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                    <FaInfoCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Cannot suspend activity</p>
                      <p className="text-xs mt-1">This activity has {activeProjects} active project(s). Suspend or complete all active projects before suspending the activity.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Projects List (if any) */}
              {activity.projects && activity.projects.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Associated Projects</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-32 overflow-y-auto">
                    {activity.projects.map((project) => (
                      <div key={project.id} className="flex justify-between items-center py-1">
                        <span className="text-sm text-gray-900 truncate">{project.project_name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${project.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : project.status === 'SUSPENDED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                          }`}>
                          {project.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={loading || !hasChanges || !formData.activity_name.trim()}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FaSpinner className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Update Activity
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};